import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function IntakeFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  // P0.2 - Payment gating state
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    adresse: '',
    telephone: '',
    email: '',
    problem_type: 'administratif',
    que_sest_il_passe: '',
    quand: '',
    qui_concerne: '',
    que_souhaitez_vous: '',
    description_libre: ''
  });
  const [files, setFiles] = useState([]);
  const [expressDelivery, setExpressDelivery] = useState(false);
  const [dossierId, setDossierId] = useState(null);
  const [loading, setLoading] = useState(false);

  // P0.2 - Verify payment on mount
  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        toast.error('Session de paiement manquante');
        navigate('/order');
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/payments/verify/${sessionId}`);
        
        if (response.data.is_paid) {
          setPaymentVerified(true);
          setPaymentInfo(response.data);
          
          // Pre-fill email if available
          if (response.data.client_email) {
            setFormData(prev => ({ ...prev, email: response.data.client_email }));
          }
          
          // Set express delivery if purchased
          if (response.data.express_upsell) {
            setExpressDelivery(true);
          }
        } else {
          toast.error('Paiement non confirmé. Veuillez compléter votre paiement.');
          navigate('/payment');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        toast.error('Erreur de vérification du paiement');
        navigate('/order');
      } finally {
        setVerifyingPayment(false);
      }
    };
    
    verifyPayment();
  }, [sessionId, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 10) {
      alert('Maximum 10 fichiers');
      return;
    }
    setFiles([...files, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const nextStep = () => {
    if (step < 6) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Create dossier
      const response = await axios.post(`${API_URL}/api/dossiers`, {
        intake_answers: formData,
        express_delivery: expressDelivery
      });
      
      const newDossierId = response.data.dossier_id;
      setDossierId(newDossierId);
      
      // Upload files
      for (const file of files) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        await axios.post(
          `${API_URL}/api/dossiers/${newDossierId}/documents`,
          uploadFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      // Navigate to thank you page
      navigate('/merci', {
        state: {
          dossierId: newDossierId,
          prenom: formData.prenom,
          email: formData.email,
          expressDelivery: expressDelivery,
          whatsappUpsell: paymentInfo?.whatsapp_upsell || false
        }
      });
    } catch (error) {
      console.error('Error creating dossier:', error);
      alert('Erreur lors de la création du dossier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container-narrow">
        {/* P0.2 - Payment Verification Loading */}
        {verifyingPayment && (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Vérification du paiement...</p>
          </div>
        )}
        
        {/* P0.2 - Show form only if payment verified */}
        {!verifyingPayment && paymentVerified && (
          <>
            {/* Payment Info Banner */}
            {paymentInfo && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-900">
                  ✅ Paiement confirmé : <strong>CHF {paymentInfo.amount}</strong>
                  {paymentInfo.whatsapp_upsell && <span className="ml-3">• WhatsApp Conseiller inclus</span>}
                  {paymentInfo.express_upsell && <span className="ml-3">• Livraison Express 24h</span>}
                </p>
              </div>
            )}
            
            {/* Progress Indicator */}
            <div className="progress-stepper mb-8">
              {[1, 2, 3, 4, 5, 6].map((s) => (
            <div key={s} className={`progress-step ${step === s ? 'active' : ''} ${step > s ? 'completed' : ''}`}>
              <div className="step-number">{s}</div>
            </div>
          ))}
        </div>

        <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
          <h2 className="text-2xl font-bold mb-6" data-testid="form-title">
            {step === 1 && 'Vos informations'}
            {step === 2 && 'Type de problème'}
            {step === 3 && 'Décrivez votre situation'}
            {step === 4 && 'Documents'}
            {step === 5 && 'Récapitulatif'}
            {step === 6 && 'Finalisation'}
          </h2>

          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Prénom *</label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  data-testid="input-prenom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Nom *</label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  data-testid="input-nom"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Adresse *</label>
                <input
                  type="text"
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  data-testid="input-adresse"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Téléphone *</label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  data-testid="input-telephone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                  data-testid="input-email"
                />
              </div>
            </div>
          )}

          {/* Step 2: Problem Type */}
          {step === 2 && (
            <div className="space-y-4">
              <label className="block text-sm font-medium mb-4">Choisissez le type de problème *</label>
              <div className="grid gap-3">
                {[
                  { value: 'chomage', label: 'Chômage' },
                  { value: 'logement', label: 'Logement' },
                  { value: 'employeur', label: 'Employeur' },
                  { value: 'administratif', label: 'Administratif' },
                  { value: 'autre', label: 'Autre' }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setFormData({ ...formData, problem_type: type.value })}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      formData.problem_type === type.value
                        ? 'border-accent bg-accent/5'
                        : 'border-border hover:border-accent/50'
                    }`}
                    data-testid={`problem-type-${type.value}`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Description */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Que s'est-il passé ? *</label>
                <textarea
                  name="que_sest_il_passe"
                  value={formData.que_sest_il_passe}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Expliquez simplement avec vos mots..."
                  required
                  data-testid="textarea-que-sest-il-passe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Quand ? *</label>
                <input
                  type="text"
                  name="quand"
                  value={formData.quand}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Ex: en décembre 2025"
                  required
                  data-testid="input-quand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Qui est concerné ? *</label>
                <input
                  type="text"
                  name="qui_concerne"
                  value={formData.qui_concerne}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Ex: moi, mon employeur, la caisse de chômage"
                  required
                  data-testid="input-qui-concerne"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Que souhaitez-vous obtenir ? *</label>
                <textarea
                  name="que_souhaitez_vous"
                  value={formData.que_souhaitez_vous}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Ex: continuer à recevoir mes indemnités"
                  required
                  data-testid="textarea-que-souhaitez-vous"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Informations complémentaires (optionnel)</label>
                <textarea
                  name="description_libre"
                  value={formData.description_libre}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="Ajoutez ici tout ce qui vous semble important..."
                  data-testid="textarea-description-libre"
                />
              </div>
            </div>
          )}

          {/* Step 4: Documents */}
          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Ajoutez les documents que vous avez reçus (maximum 10 fichiers, PDF ou images)
              </p>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-input"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer"
                >
                  <div className="text-4xl mb-2">📄</div>
                  <p className="font-medium">Cliquez pour ajouter des fichiers</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    PDF, JPG, PNG (max 50MB par fichier)
                  </p>
                </label>
              </div>
              
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="font-medium">{files.length} fichier(s) ajouté(s):</p>
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                      <span className="text-sm">{file.name}</span>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-destructive hover:text-destructive/80"
                        data-testid={`remove-file-${index}`}
                      >
                        Supprimer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 5: Summary */}
          {step === 5 && (
            <div className="space-y-4">
              <div className="bg-secondary/50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Vos informations</h3>
                <p className="text-sm">{formData.prenom} {formData.nom}</p>
                <p className="text-sm">{formData.email}</p>
              </div>
              <div className="bg-secondary/50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Votre situation</h3>
                <p className="text-sm">{formData.que_sest_il_passe.substring(0, 200)}...</p>
              </div>
              <div className="bg-secondary/50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Documents</h3>
                <p className="text-sm">{files.length} fichier(s) ajouté(s)</p>
              </div>
            </div>
          )}

          {/* Step 6: Final */}
          {step === 6 && (
            <div className="text-center space-y-6">
              <div className="text-6xl">✅</div>
              <h3 className="text-2xl font-bold">Prêt à finaliser votre demande</h3>
              <p className="text-muted-foreground">
                Vous allez être redirigé vers le paiement sécurisé
              </p>
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-6">
                <div className="text-3xl font-bold text-accent mb-2">
                  CHF {expressDelivery ? '68' : '49'}
                </div>
                <p className="text-sm text-muted-foreground">
                  {expressDelivery ? (
                    <>
                      Base CHF 49 + Express CHF 19
                      <br />
                      <span className="font-semibold text-amber-600">⚡ Livraison en 12h</span>
                    </>
                  ) : (
                    <>
                      Paiement unique – Livraison 24-48h
                    </>
                  )}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                ⚠️ Assistance rédactionnelle uniquement – pas de conseil juridique
              </p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            {step > 1 && (
              <button
                onClick={prevStep}
                className="px-6 py-2 border border-border rounded-lg hover:bg-secondary"
                disabled={loading}
                data-testid="btn-prev"
              >
                Précédent
              </button>
            )}
            
            {step < 6 && (
              <button
                onClick={nextStep}
                className="ml-auto px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent/90"
                data-testid="btn-next"
              >
                Suivant
              </button>
            )}
            
            {step === 6 && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="ml-auto px-8 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50"
                data-testid="btn-submit"
              >
                {loading ? 'Création en cours...' : 'Soumettre ma demande'}
              </button>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default IntakeFormPage;
